import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./WallsTab.css";
import WallSelector, { UiWall } from "./components/WallSelector";
import { useConfiguration } from "src/contexts/ConfigurationContext";
import { Wall } from "src/types/configuration";
// @ts-ignore
import isEqual from "lodash/isEqual";
import Text from "src/components/Text/Text";
import { useTabScroll } from "src/contexts/TabScrollContext";
import HorizontalSwitch from "src/components/Input/HorizontalSwitch/HorizontalSwitch";
import SelectUnselectExtended from "src/components/Input/SelectUnselectExtended/SelectUnselectExtended";

const glassShadesDoors = [
  {
    id: 0,
    name: "Standaard",
    value: "light",
    imageUrl: `${process.env.PUBLIC_URL}/images/ver_door_light.png`,
    price: 0,
    description:
      "",
  },
  {
    id: 1,
    name: "Steellook",
    value: "steellook",
    imageUrl: `${process.env.PUBLIC_URL}/images/ver_window_light.jpg`,
    price: 0,
    description:
      "",
  },
  {
    id: 2,
    name: "Verduisterend",
    value: "dark",
    imageUrl: `${process.env.PUBLIC_URL}/images/ver_door_dark.png`,
    price: 0,
    description:
      "",
  },
];

const glassProfileColors = [
  {
    id: 0,
    name: "Zwart",
    value: "zwart",
  },
  {
    id: 1,
    name: "Antraciet",
    value: "antraciet",
  },
  {
    id: 2,
    name: "Wit",
    value: "wit",
  },
];

interface WallComponentProps {
  wall: Wall;
  onWallChange: (wall: Wall) => void;
}

const WoodWallComponent: React.FC<WallComponentProps> = ({
  wall,
  onWallChange,
}) => {
  const { state, configurator, configuration, setSpy } = useConfiguration();
  const getOuterColorValueForUi = () =>
    wall.woodWall.outerColor === "window_2x2Tracks"
      ? "door_3x3Tracks"
      : wall.woodWall.outerColor;
  const [outerColorValue, setOuterColorValue] = useState(getOuterColorValueForUi());

  useEffect(() => {
    setOuterColorValue(getOuterColorValueForUi());
  }, [wall.woodWall.outerColor]);

  const getSlidingGlassShade = () => {
    if (wall.woodWall.outerColor.startsWith("window")) return "steellook";
    return wall.glassDark ? "dark" : "light";
  };

  const isSteellookSelected = getSlidingGlassShade() === "steellook";

  const handleSideEnableChange = (sideEnable: boolean) => {
    if ((wall.slidingGlassWallOptions.schuifrichting !== "empty") === sideEnable)
      return;

    const slidingGlassWallOptions = { ...wall.slidingGlassWallOptions };

    if (sideEnable) {
      slidingGlassWallOptions.schuifrichting =
        slidingGlassWallOptions.schuifrichting === "empty"
          ? "left"
          : slidingGlassWallOptions.schuifrichting;
    } else {
      slidingGlassWallOptions.schuifrichting = "empty";
    }

    const updateWall = { ...wall, slidingGlassWallOptions };
    if (!isEqual(wall, updateWall)) onWallChange(updateWall);
  };

  const renderSide = useRef<() => React.ReactElement>(null!);
  renderSide.current = () => (
    <HorizontalSwitch
      value={wall.orientation}
      values={[
        { id: 0, label: "Links", value: "left" },
        { id: 1, label: "Midden", value: "middle" },
        { id: 2, label: "Rechts", value: "right" },
      ]}
      onChange={handleOrientationChange}
      disabled={isSteellookSelected}
    />
  );
  const SideComponent: React.FC = useCallback(() => renderSide.current(), []);

  const handleGlassColorEnableChange = (colorEnable: boolean) => {
    if ((wall.slidingGlassWallOptions.glassColor !== "empty") === colorEnable)
      return;

    const slidingGlassWallOptions = { ...wall.slidingGlassWallOptions };

    if (colorEnable) {
      slidingGlassWallOptions.glassColor =
        slidingGlassWallOptions.glassColor === "empty"
          ? "zwart"
          : slidingGlassWallOptions.glassColor;
      slidingGlassWallOptions.glasbok = 1;
    } else {
      slidingGlassWallOptions.glassColor = "empty";
      slidingGlassWallOptions.glasbok = 0;
    }

    const updateWall = { ...wall, slidingGlassWallOptions };
    if (!isEqual(wall, updateWall)) onWallChange(updateWall);
  };

  const renderKleur = useRef<() => React.ReactElement>(null!);
  renderKleur.current = () => (
    <HorizontalSwitch
      value={getSlidingGlassShade()}
      values={glassShadesDoors.map((option) => ({
        id: option.id,
        label: option.name,
        value: option.value,
      }))}
      onChange={handleSlidingGlassShadeChange}
    />
  );
  const KleurComponent: React.FC = useCallback(() => renderKleur.current(), []);

  const getGlassProfileColor = () =>
    wall.slidingGlassWallOptions.glassColor === "empty"
      ? "zwart"
      : wall.slidingGlassWallOptions.glassColor;

  const handleGlassProfileColorChange = (value: string) => {
    if (wall.slidingGlassWallOptions.glassColor === value) return;

    const updateWall = {
      ...wall,
      slidingGlassWallOptions: {
        ...wall.slidingGlassWallOptions,
        glassColor: value,
      },
    };
    if (!isEqual(wall, updateWall)) onWallChange(updateWall);
  };

  const renderProfileColor = useRef<() => React.ReactElement>(null!);
  renderProfileColor.current = () => (
    <HorizontalSwitch
      value={getGlassProfileColor()}
      values={glassProfileColors.map((option) => ({
        id: option.id,
        label: option.name,
        value: option.value,
      }))}
      onChange={handleGlassProfileColorChange}
    />
  );
  const ProfileColorComponent: React.FC = useCallback(
    () => renderProfileColor.current(),
    []
  );

  const slidingGlassWallOptions = [
    {
      name: "Schuifrichting",
      imageUrl: `${process.env.PUBLIC_URL}/images/space-option-thumb-4.png`,
      description:
        "Geef hier de gewenste schuifrichting op van uw glazen schuifwand. Geef dit op vanaf de positie als u er vanaf de buitenzijde recht voorstaat.",
      Component: SideComponent,
      getIsSelected: () =>
        wall.slidingGlassWallOptions.schuifrichting !== "empty",
      onSelect: () => handleSideEnableChange(true),
      onUnselect: () => handleSideEnableChange(false),
      deselectByCheckboxOnly: false,
    },
    {
      name: "Type",
      imageUrl: `${process.env.PUBLIC_URL}/images/space-option-thumb-1.png`,
      description:
        "Selecteer het gewenste type glas voor uw glazen schuifwand.",
      Component: KleurComponent,
      getIsSelected: () => true,
      onSelect: () => undefined,
      onUnselect: () => undefined,
      deselectByCheckboxOnly: true,
    },
    {
      name: "Kleur profielen",
      imageUrl: `${process.env.PUBLIC_URL}/images/space-option-thumb-1.png`,
      description:
        "Selecteer de gewenste kleur voor uw glazen schuifwand profielen.",
      Component: ProfileColorComponent,
      getIsSelected: () => wall.slidingGlassWallOptions.glassColor !== "empty",
      onSelect: () => handleGlassColorEnableChange(true),
      onUnselect: () => handleGlassColorEnableChange(false),
      deselectByCheckboxOnly: false,
    },
  ];

  const renderDoor = useRef<() => React.ReactElement>(null!);
  renderDoor.current = () => {
    return (
      <>
        <div className="sliding-glass-wall-options">
          <Text
            content={"Opties Glazen schuifwand"}
            className={"sliding-glass-wall-options-title"}
          />
          <Text
            content={
              "De glazen schuifwanden worden compleet geleverd, incl. glaswanden, profielen, meenemers, deurgreep en tochtprofielen."
            }
            className={"sliding-glass-wall-options-subtitle"}
          />
          {slidingGlassWallOptions.map((option) => (
            <SelectUnselectExtended
              key={option.name}
              name={option.name}
              imageUrl={option.imageUrl}
              description={option.description}
              Component={option.Component}
              isSelected={option.getIsSelected()}
              onSelect={option.onSelect}
              onUnselect={option.onUnselect}
              deselectByCheckboxOnly={option.deselectByCheckboxOnly}
              stopPropagation={true}
              scrollOnToggle={false}
            />
          ))}
        </div>
      </>
    );
  };
  const DoorComponent: React.FC = useCallback(() => renderDoor.current(), []);

  const sideWall =
    wall.id === `v-0-${configurator.getColumns()}` || wall.id === `v-0-0`
      ? true
      : false;

  const outerColors = useMemo(
    () => [
      {
        id: 0,
        name: "Glazen schuifwand",
        imageUrl: `${process.env.PUBLIC_URL}/images/ver-door-screenshot.png`,
        color: "",
        value: "door_3x3Tracks",
        price: 0,
        description:
          "",
        component: DoorComponent,
        selectedNames: glassShadesDoors.find((i) => i.value === getSlidingGlassShade())
          ?.name,
        carouselImages: glassShadesDoors.map((i) => i.imageUrl),
      },
      {
        id: 1,
        name: "Aluminium wand",
        imageUrl: `${process.env.PUBLIC_URL}/images/ver-alum-screenshot.png`,
        color: "",
        value: "aluminiumWall",
        price: 0,
        description:
          "Een aluminium zijwand in je veranda zorgt voor een strakke look, houdt de wind tegen en vraagt geen onderhoud. Dankzij de aluminium rabatdelen die naadloos op elkaar klikken, ontstaat er een volledig dichte en strakke wand. Je zit altijd beschut en geniet langer van je buitenruimte.",
      },
    ],
    [configurator.getColumns(), DoorComponent, wall.glassDark, wall.woodWall.outerColor]
  );

  window.CONF.house.walls.map((item) => {
    if (
      (wall.position.end.x === item.end.x &&
        wall.position.end.y === item.end.y &&
        wall.position.start.x === item.start.x &&
        wall.position.start.y === item.start.y) ||
      (wall.position.end.x === item.start.x &&
        wall.position.end.y === item.start.y &&
        wall.position.start.x === item.end.x &&
        wall.position.start.y === item.end.y)
    ) {
      const cols = configurator.getColumns();
      const isLeft = wall.position.start.x === 0 && wall.position.end.x === 0;
      const isRight =
        wall.position.start.x === cols && wall.position.end.x === cols;

      const length = item.sections[0].length;
      const calculation = configuration.calculationService;

      outerColors.map((localitem) => {
        if (localitem.value === "aluminiumWall")
          localitem.price = calculation.applyMontage(
            calculation.GetAlum(length, isLeft, isRight)
          );

        if (localitem.value === "door_3x3Tracks")
          localitem.price = calculation.applyMontage(
            isSteellookSelected
              ? calculation.getWindowsPrice(length)
              : calculation.getSlidingDoorsPrice(
                  length,
                  wall.glassDark ? "dark" : "light"
                )
          );
      });
    }
  });

  const handleOuterColor = (outerColor: string) => {
    if (outerColor === "") return;
    setOuterColorValue(outerColor);

    const isSlidingGlassSelection = outerColor === "door_3x3Tracks";
    const hasSlidingGlassSelected =
      wall.woodWall.outerColor.startsWith("door") ||
      wall.woodWall.outerColor.startsWith("window");

    if (isSlidingGlassSelection) {
      if (hasSlidingGlassSelected) return;
    }
    if (wall.woodWall.outerColor === outerColor) return;
    const woodWall = { ...wall.woodWall };
    woodWall.outerColor = outerColor;
    const columns = configurator.getColumns();

    const id = wall.id;
    const leftwall = id === "v-0-0";
    const rightwall = id === `v-0-${columns}`;
    const alumwall = outerColor.startsWith("alum");
    if (leftwall && !alumwall) setSpy({ ...state.spy, left: true });
    else if (rightwall && !alumwall) setSpy({ ...state.spy, right: true });

    const updateWall = { ...wall, woodWall: woodWall, glassDark: false };
    if (!isEqual(wall, updateWall)) onWallChange(updateWall);

  };

  const handleOrientationChange = (value: string) => {
    if (!isEqual(wall.orientation, value))
      onWallChange({ ...wall, orientation: value });
  };

  const isOuterColorSelected = (value: string) =>
    value === "door_3x3Tracks"
      ? wall.woodWall.outerColor.startsWith("door") ||
      wall.woodWall.outerColor.startsWith("window")
      : wall.woodWall.outerColor === value;

  const handleOuterColorUnselect = (value: string) => {
    if (!isOuterColorSelected(value)) return;
    setOuterColorValue("");

    const columns = configurator.getColumns();
    if (wall.id === "v-0-0" && state.spy.left)
      setSpy({ ...state.spy, left: false });
    else if (wall.id === `v-0-${columns}` && state.spy.right)
      setSpy({ ...state.spy, right: false });

    const updateWall = {
      ...wall,
      woodWall: { ...wall.woodWall, outerColor: "empty" },
      glassDark: false,
    };
    if (!isEqual(wall, updateWall)) onWallChange(updateWall);
  };

  const handleSlidingGlassShadeChange = (value: string) => {
    const current = wall.woodWall.outerColor;
    if (!current.startsWith("door") && !current.startsWith("window")) return;

    const updateWall = { ...wall, woodWall: { ...wall.woodWall } };

    if (value === "steellook") {
      updateWall.woodWall.outerColor = "window_2x2Tracks";
      updateWall.glassDark = false;
    } else {
      updateWall.woodWall.outerColor = "door_3x3Tracks";
      updateWall.glassDark = value === "dark";
    }

    if (!isEqual(wall, updateWall)) onWallChange(updateWall);
  };

  return (
    <>
      {(sideWall
        ? outerColors
        : outerColors.filter((value) => value.value !== "aluminiumWall")
      ).map((option) => (
        <SelectUnselectExtended
          key={option.value}
          name={option.name}
          imageUrl={option.imageUrl}
          color={option.color}
          price={option.price}
          description={option.description}
          Component={option.component}
          isSelected={isOuterColorSelected(option.value)}
          onSelect={() => handleOuterColor(option.value)}
          onUnselect={() => handleOuterColorUnselect(option.value)}
          selectedNames={option.selectedNames}
          carouselImages={option.carouselImages}
          showCheckbox={true}
          stopPropagation={true}
        />
      ))}

    </>
  );
};

const WallsTab: React.FC = () => {
  const { state, addWall, setWalls, configuration, configurator, setSpy } =
    useConfiguration();
  const [selectedWall, setSelectedWall] = useState<UiWall | null>(null);
  const [activeWalls, setActiveWalls] = useState<string[]>([]);
  const [storageWalls, setStorageWalls] = useState<string[]>([]);
  const [wall, setWall] = useState<Wall>(
    configuration.findWallbyUiWall(selectedWall)
  );
  const { scrollTo } = useTabScroll();

  const handleSelectedWall = (newUiWall: UiWall | null) => {
    if (!wall) return;

    const newWall = configuration.findWallbyUiWall(newUiWall);

    if (newWall.storage !== "empty") {
      return;
    }

    const columns = configurator.getColumns();

    if (newUiWall?.id === `v-0-${columns}`) {
      newWall.isRightSide = true;
    }

    setSelectedWall(newUiWall);
    setWall(newWall);

    configurator.focusOnWall(newWall);
    configurator.selectWall(newWall);

    setTimeout(() => {
      const tabElement = document.querySelector(`.walls-tab .wall-wood-wall`);
      if (tabElement) scrollTo(tabElement);
    }, 80);
  };

  const isSelectedSideWall =
    !!selectedWall &&
    (wall.id === "v-0-0" || wall.id === `v-0-${configurator.getColumns()}`);

  const handleWallChange = (updatedWall: Wall) => {
    if (!wall) return;

    if (!isEqual(wall, updatedWall)) {
      addWall(updatedWall);
      setWall(updatedWall);
    }
  };

  const handleWallDelete = (newUiWall: UiWall | null) => {
    setSelectedWall(null);
    configurator.deselectWalls();
    if (wall.id === "v-0-0") {
      setSpy({ ...state.spy, left: false });
    }
    if (wall.id === `v-0-${configurator.getColumns()}`) {
      setSpy({ ...state.spy, right: false });
    }

    if (!wall) return;
    if (wall.storage === "empty")
      setWalls(state.walls.filter((wall) => wall.id !== newUiWall?.id));
  };

  useEffect(() => {
    if (!wall) return;

    setActiveWalls(
      state.walls
        .filter((wall) => {
          if (wall.storage !== "empty") return false;
          if (wall.slidingGlassWall !== "empty") return true;
          return wall.woodWall.outerColor !== "empty";
        })
        .map((wall) => wall.id)
    );

    setStorageWalls(
      state.walls
        .filter((wall) => {
          return wall.storage !== "empty";
        })
        .map((wall) => wall.id)
    );
  }, [state.walls, wall]);

  const getspy = () => {
    const left: boolean = state.spy.left;
    const right: boolean = state.spy.right;

    return left && right
      ? "both"
      : !left && !right
      ? "none"
      : right
      ? "rechts"
      : "links";
  };

  const handleSpyState = (left: boolean, right: boolean) => {
    if (state.spy.left === left && state.spy.right === right) return;
    setSpy({ ...state.spy, left: left, right: right });
  };

  const handleSpy = (value: string) => {
    if (value === "none") {
      handleSpyState(false, false);
    } else if (value === "both") {
      handleSpyState(true, true);
    } else if (value === "rechts") {
      handleSpyState(false, true);
    } else if (value === "links") {
      handleSpyState(true, false);
    }
  };

  const spies = [
    {
      id: 0,
      label: "Geen",
      value: "none",
    },
    { id: 1, label: "Links", value: "links" },
    { id: 2, label: "Rechts", value: "rechts" },
    { id: 3, label: "Links & rechts", value: "both" },
  ];

  const SpyComponent = () => {
    return (
      <>
        <HorizontalSwitch
          value={getspy()}
          values={spies}
          onChange={handleSpy}
        />
      </>
    );
  };

  return (
    <div className="walls-tab">
      <div className="walls-selector-container">
        <WallSelector
          onWallSelect={handleSelectedWall}
          onWallDeselect={handleWallDelete}
          activeWalls={activeWalls}
          storageWalls={storageWalls}
        />
      </div>

      {selectedWall && (
        <WoodWallComponent wall={wall} onWallChange={handleWallChange} />
      )}
      {state.roofType === "oase" && isSelectedSideWall && (
        <>
          <SelectUnselectExtended
            name="Spie optie"
            imageUrl={`${process.env.PUBLIC_URL}/images/ver_spie.png`}
            description=""
            isSelected={state.spy.left || state.spy.right}
            onSelect={() => {
              if (getspy() === "none") handleSpy("both");
            }}
            onUnselect={() => handleSpy("none")}
            Component={SpyComponent}
            showCheckbox={true}
            deselectByCheckboxOnly={true}
            alwaysShowSelected={true}
            selectedNames={
              spies.find((i) => i.value === getspy() && i.value !== "none")
                ?.label || ""
            }
            price={configuration.calculationService.getSurcharge(
              { spy: state.spy },
              { spy: { ...state.spy, left: false, right: false } }
            )}
          />
        </>
      )}
    </div>
  );
};

export default WallsTab;
